import './userLocationMarkerElement.css'

import type maplibregl from 'maplibre-gl'

/**
 * `passive` = the small circle shown whenever a position is known; `tracking` = the larger pulsing
 * marker of the active follow/geolocate mode. Both share the heading arrow.
 */
export type UserLocationMarkerVariant = 'passive' | 'tracking'

const SVG_NS = 'http://www.w3.org/2000/svg'

/**
 * Glyph geometry lives in a 36×36 viewBox whose centre (18, 18) is the dot centre — and therefore
 * both the MapLibre anchor and the rotation origin. The arrow's base sits *inside* the dot, so the
 * shared white halo wraps dot and arrow as one silhouette (rather than a triangle parked behind a
 * circle) while the dot's own ring keeps the wedge legible as a separate heading cue. The extreme
 * point (arrow tip plus halo) stays within 14 units of the centre, so nothing clips at any rotation.
 */
const HEADING_ARROW_PATH = 'M14.1 16.6 L18 6.6 L21.9 16.6 Z'
const DOT_CENTRE = '18'
const DOT_RADIUS = '4.5'

/**
 * One paint pass of the glyph: arrow + dot. Painting the pair twice — first the halo (white,
 * fattened by its stroke), then the body on top — is what gives the union a single continuous white
 * outline with no seam where the arrow meets the dot.
 */
const createGlyphLayer = (layerClass: string): SVGGElement => {
  const layer = document.createElementNS(SVG_NS, 'g')
  layer.setAttribute('class', layerClass)

  const arrow = document.createElementNS(SVG_NS, 'path')
  arrow.setAttribute('class', 'location-marker-heading')
  arrow.setAttribute('d', HEADING_ARROW_PATH)
  layer.appendChild(arrow)

  const dot = document.createElementNS(SVG_NS, 'circle')
  dot.setAttribute('class', 'location-marker-dot')
  dot.setAttribute('cx', DOT_CENTRE)
  dot.setAttribute('cy', DOT_CENTRE)
  dot.setAttribute('r', DOT_RADIUS)
  layer.appendChild(dot)

  return layer
}

/**
 * Builds the DOM for the user's own position: a white-outlined dot that grows a heading arrow out
 * of its own outline, plus (tracking variant only) the pulse ring. Single source of truth so the
 * map and the styleguide render the identical marker.
 */
export const createUserLocationMarkerElement = (
  variant: UserLocationMarkerVariant,
): HTMLDivElement => {
  const marker = document.createElement('div')
  marker.className =
    variant === 'passive' ? 'passive-user-location-marker' : 'custom-user-location-marker'

  if (variant === 'tracking') {
    const pulse = document.createElement('div')
    pulse.className = 'location-marker-pulse'
    marker.appendChild(pulse)
  }

  const glyph = document.createElementNS(SVG_NS, 'svg')
  glyph.setAttribute('class', 'location-marker-glyph')
  glyph.setAttribute('viewBox', '0 0 36 36')
  glyph.setAttribute('aria-hidden', 'true')
  glyph.setAttribute('focusable', 'false')
  glyph.appendChild(createGlyphLayer('location-marker-halo'))
  glyph.appendChild(createGlyphLayer('location-marker-body'))
  marker.appendChild(glyph)

  return marker
}

/**
 * Accepts only a usable compass bearing in [0, 360] and folds 360 back to 0, so an unknown heading
 * (GPS reports `null` while standing still, the compass before the first reading) hides the arrow
 * instead of pointing north.
 */
export const normalizeLocationHeading = (heading: number | null | undefined): number | null => {
  if (typeof heading !== 'number' || !Number.isFinite(heading) || heading < 0 || heading > 360) {
    return null
  }
  return heading === 360 ? 0 : heading
}

/**
 * Shows/hides the heading arrow on a marker element and returns the normalized bearing (`null` when
 * there is no usable heading). Use this when the rotation is applied by something other than
 * MapLibre — otherwise use `updateUserLocationMarkerHeading`.
 */
export const setUserLocationMarkerHeading = (
  element: HTMLElement,
  heading: number | null | undefined,
): number | null => {
  const normalizedHeading = normalizeLocationHeading(heading)
  element.classList.toggle('has-heading', normalizedHeading !== null)
  return normalizedHeading
}

/** Applies a heading to a MapLibre marker: arrow visibility plus map-aligned rotation. */
export const updateUserLocationMarkerHeading = (
  marker: maplibregl.Marker,
  heading: number | null | undefined,
): void => {
  const normalizedHeading = setUserLocationMarkerHeading(marker.getElement(), heading)
  marker.setRotation(normalizedHeading ?? 0)
}
